import React, { useCallback, useEffect, useState } from 'react'
import { Doc, DocumentEditorProps, DocumentProps, UploadingFileProps } from './types'
import { JSONFetcher, LogoSpinner, handleError, themeColor } from 'src/shared'
import { Dropzone } from 'src/shared/dropzone'

export const DocumentEditor = <T extends Doc>(props: DocumentEditorProps): JSX.Element => {
  const [documents, setDocuments] = useState<Array<T>>([])
  const [uploadingFiles, setUploadingFiles] = useState<Array<File>>([])

  const handleUpload = useCallback((acceptedFiles: File[]) => {
    setUploadingFiles((old) => old.concat(acceptedFiles))
  }, [])

  useEffect(() => {
    JSONFetcher.load({ url: props.indexUrl, success: setDocuments })
  }, [props.indexUrl])

  return (
    <div>
      <Dropzone
        accept={['image/jpg', 'image/jpeg', 'image/tif', 'image/tiff', 'image/png', 'application/pdf']}
        multiple={true}
        maxSize={20000000}
        handleUpload={handleUpload}
      />
      {uploadingFiles.map((file, index) => (
        <UploadingFile file={file} onUpload={setDocuments} url={props.indexUrl} key={index} />
      ))}
      {documents.map((d, index) => (
        <Document {...d} key={index} onDelete={setDocuments} />
      ))}
    </div>
  )
}

const UploadingFile = <T extends Doc>({ file, onUpload, url }: UploadingFileProps<T>): JSX.Element | null => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    const body = new FormData()
    body.append('file', file)

    JSONFetcher.call<FormData, T[]>({
      url: url,
      method: 'post',
      body,
      config: {
        withCredentials: true,
        headers: {
          'Content-Type': undefined,
        },
      },
    })
      .then((data) => {
        onUpload(data)
        setLoading(false)
      })
      .catch(handleError)
  }, [onUpload, url, file])

  if (!loading) return null

  return (
    <div className="flex items-center">
      <div className="modules-document-editor--thumb flex items-center justify-center">
        <div className="flex items-center">
          <LogoSpinner smallSpinner color={themeColor('green-500')} />
        </div>
      </div>
      <span>{file.name}</span>
    </div>
  )
}

const Document = <T extends Doc>({ onDelete, loading, url, mimetype, filename }: DocumentProps<T>): JSX.Element => {
  let thumb = (
    <div className="flex items-center">
      <LogoSpinner smallSpinner color={themeColor('green-500')} />
    </div>
  )

  if (!loading) {
    thumb = (
      <div className="modules-document-editor--thumb-inner flex justify-center">{(mimetype ?? '').split('/')[1]}</div>
    )
  }

  const deleteDocument = useCallback(() => {
    if (window.confirm(i18n.t('modules.document_editor.are_you_sure_to_delete'))) {
      JSONFetcher.delete({
        url: url,
        success: (data) => onDelete(data),
      })
    }
  }, [url, onDelete])

  return (
    <div className="flex items-center">
      <div className="modules-document-editor--thumb flex items-center justify-center">{thumb}</div>
      <a target="_blank" href={url} rel="noreferrer">
        {filename}
      </a>
      <button onClick={deleteDocument}>
        <i className="fa fa-trash-alt fa-md ml-2"></i>
      </button>
    </div>
  )
}
